import React, { useMemo, useState } from 'react';

import { ExploreBlockType, UpsertExploreBlockRequest } from '@addmile/backend-client';

import { useContentQueries } from '../services/ContentManagerService';
import BlockForm from './BlockForm/BlockForm';
import Previewer from './Previewer/Previewer';

interface ContentEditorViewProps {
  editingSlug?: string;
  draftType?: ExploreBlockType;
  onBack: () => void;
  onSaveSuccess: () => void;
}

export const ContentEditorView: React.FC<ContentEditorViewProps> = ({
  editingSlug,
  draftType,
  onBack,
  onSaveSuccess,
}) => {
  const [previewText, setPreviewText] = useState('');
  const [previewMeta, setPreviewMeta] = useState<{
    type?: ExploreBlockType;
    videoUrl?: string;
  }>({});

  const { useBlock, useUpsertBlock } = useContentQueries();
  const { isLoading: isLoadingBlock } = useBlock(editingSlug || '', !!editingSlug);
  const upsertMutation = useUpsertBlock();

  const initialBlockData = useMemo(() => {
    if (editingSlug) return undefined;
    if (draftType)
      return { type: draftType as ExploreBlockType } as Partial<UpsertExploreBlockRequest>;
    return undefined;
  }, [editingSlug, draftType]);

  const handleSaveBlock = async ({
    slug,
    data,
  }: {
    slug: string;
    data: UpsertExploreBlockRequest;
  }) => {
    await upsertMutation.mutateAsync({ slug, data });
  };

  const handleCancelEdit = () => {
    onBack();
  };

  return (
    <div className="max-w-[944px] p-6 rounded-2xl">
      <div className="space-y-6 bg-white-100 rounded-2xl p-6 shadow-s">
        <BlockForm
          slug={editingSlug}
          initialData={initialBlockData}
          onSubmit={handleSaveBlock}
          onCancel={handleCancelEdit}
          onContentChange={setPreviewText}
          onMetaChange={setPreviewMeta}
          isLoading={isLoadingBlock || upsertMutation.isPending}
          editingSlug={editingSlug}
          onSaveSuccess={onSaveSuccess}
        />

        <div className="space-y-4">
          <h3 className="text-lg font-medium text-primary-90 bg-primary-10 rounded-lg p-2 w-fit">
            Live Content Preview
          </h3>
          <div className="py-4 bg-gray-50 min-h-[400px]">
            <Previewer
              value={previewText}
              debounceMs={300}
              mediaType={previewMeta.type === 'video' ? 'video' : undefined}
              mediaUrl={previewMeta.type === 'video' ? previewMeta.videoUrl : undefined}
              config={{
                enableAnimations: true,
                spacing: 'normal',
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentEditorView;
